import {
  useTransform,
  m,
  type MotionValue,
  LazyMotion,
  domAnimation,
} from "framer-motion";
import { useEffect, useMemo, useRef } from "react";
import { Dc_Card_Sticky } from "@op/sf-op";
import {
  cls,
  makeEditorImageUrl,
  makeRendererImageUrl,
  MediaPreview,
  useRender,
} from "@op/utils";
import type { StickyCardProps } from "@op/puck-types";
import { LazyIcon } from "@op/icons";

type StickyCardDesktopProps = StickyCardProps & {
  idx: number;
  shrinkStartOffset: number;
  shrinkEndOffset: number;
  shrinkTarget: number;
  progress: MotionValue<number>;
  onUpdateHeight?: (height: number) => void;
  minHeight: number;
};

const StickyCardDesktop = (props: StickyCardDesktopProps) => {
  const {
    idx,
    shrinkStartOffset,
    shrinkEndOffset,
    shrinkTarget,
    progress,
    onUpdateHeight,
    minHeight: _minHeight,
    children,
    ...item
  } = props;

  const ref = useRef<HTMLDivElement>(null);

  const shrink = useTransform(
    progress,
    [shrinkStartOffset, shrinkEndOffset],
    [1, shrinkTarget]
  );

  useEffect(() => {
    if (!ref.current) return;
    onUpdateHeight?.(ref.current.getBoundingClientRect().height);
  }, [ref]);

  const { icon, medium, headline } = item;
  const { context, apiUrl } = useRender();

  const imageUrl = useMemo(() => {
    if (!medium || !medium.uploadId) return "";
    switch (context) {
      case "editor":
        return makeEditorImageUrl(medium.uploadId, apiUrl);
      case "renderer":
        return makeRendererImageUrl(medium.uploadId, apiUrl);
      default:
        return "";
    }
  }, [context, apiUrl, medium]);

  return (
    <LazyMotion strict features={domAnimation}>
      <m.div
        ref={ref}
        className={cls("sticky", "drop-shadow-md", "card-sticky-desktop")}
        style={{
          top: `${40 + idx * 10}px`,
          scale: shrink,
          transformOrigin: "top",
        }}
      >
        <Dc_Card_Sticky
          changeImagePos={idx % 2 === 0}
          imageSlot={
            medium && (
              <MediaPreview
                cover
                src={imageUrl}
                alt={medium.metadata?.caption || ""}
                mimeType={medium.mimeType}
                width={medium.width}
                height={medium.height}
                loading="lazy"
              />
            )
          }
          icon={icon && <LazyIcon icon={icon} />}
          headline={headline}
          text={children}
        />
      </m.div>
    </LazyMotion>
  );
};

const StickyCardMobile = ({
  medium,
  idx,
  icon,
  headline,
  children,
}: StickyCardDesktopProps) => {
  const { context, apiUrl } = useRender();

  const imageUrl = useMemo(() => {
    if (!medium || !medium.uploadId) return "";
    switch (context) {
      case "editor":
        return makeEditorImageUrl(medium.uploadId, apiUrl);
      case "renderer":
        return makeRendererImageUrl(medium.uploadId, apiUrl);
      default:
        return "";
    }
  }, [context, apiUrl, medium]);

  return (
    <div className="card-sticky-mobile">
      <Dc_Card_Sticky
        changeImagePos={idx % 2 === 0}
        imageSlot={
          medium && (
            <MediaPreview
              cover
              src={imageUrl}
              alt={medium.metadata?.caption || ""}
              mimeType={medium.mimeType}
              width={medium.width}
              height={medium.height}
              loading="lazy"
            />
          )
        }
        icon={icon && <LazyIcon icon={icon} />}
        headline={headline}
        text={children}
      />
    </div>
  );
};

const StickyCard = (props: StickyCardDesktopProps) => {
  return (
    <>
      <StickyCardMobile {...props} />
      <StickyCardDesktop {...props} />
    </>
  );
};

export default StickyCard;
